import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import {
  Block as BlockedIcon,
  CheckCircleOutline as CompleteIcon,
  LockOutlined as LockedIcon,
  RadioButtonUnchecked as UpcomingIcon,
  TripOrigin as CurrentIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import type { GroupedWorkflowStepperVisualStatus } from '../../domain/incidents/groupedSafetyCaseStepLabels';
import {
  SAFETY_WORKFLOW_STEPS,
  type SafetyWorkflowStepDefinition,
  type SafetyWorkflowProgress,
  type SafetyWorkflowStepId,
  safetyWorkflowStepStatus,
} from '../../domain/safetyWorkflowStepMapping';

export type SafetyWorkflowStepperProps = {
  progress: SafetyWorkflowProgress;
  compact?: boolean;
  showNextAction?: boolean;
  blockedStepIds?: import('../../domain/safetyWorkflowStepMapping').SafetyWorkflowStepId[];
  lockedStepIds?: SafetyWorkflowStepId[];
  showStatusLabels?: boolean;
  steps?: SafetyWorkflowStepDefinition[];
  statusCaption?: (stepId: SafetyWorkflowStepId, status: GroupedWorkflowStepperVisualStatus) => string;
  statusHint?: (stepId: SafetyWorkflowStepId, status: GroupedWorkflowStepperVisualStatus) => string | null;
};

function workflowStatusCaption(status: ReturnType<typeof safetyWorkflowStepStatus>): string {
  switch (status) {
    case 'complete':
      return 'Done';
    case 'current':
      return 'Current';
    case 'locked':
      return 'Locked';
    case 'blocked':
      return 'Blocked';
    default:
      return 'Pending';
  }
}

export const SafetyWorkflowStepper: React.FC<SafetyWorkflowStepperProps> = ({
  progress,
  compact = false,
  showNextAction = true,
  blockedStepIds,
  lockedStepIds,
  showStatusLabels = false,
  steps = SAFETY_WORKFLOW_STEPS,
  statusCaption,
  statusHint,
}) => {
  const theme = useTheme();

  return (
    <Box data-testid="safety-workflow-stepper">
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1, md: 0.75 }}
        alignItems={{ md: 'center' }}
        useFlexGap
        flexWrap="wrap"
      >
        {steps.map((step, index) => {
          const status = safetyWorkflowStepStatus(step.id, progress, { blockedStepIds, lockedStepIds });
          const label = compact ? step.compactLabel : step.label;
          const isCurrent = status === 'current';
          const isComplete = status === 'complete';
          const isBlocked = status === 'blocked';
          const isLocked = status === 'locked';
          const accent = isComplete
            ? theme.palette.success.main
            : isBlocked
              ? theme.palette.warning.main
              : isLocked
                ? theme.palette.text.secondary
                : isCurrent
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary;

          return (
            <React.Fragment key={step.id}>
              {index > 0 ? (
                <Typography
                  aria-hidden
                  variant="body2"
                  color="text.disabled"
                  sx={{ display: { xs: 'none', md: 'block' }, px: 0.25 }}
                >
                  →
                </Typography>
              ) : null}
              <Stack direction="column" spacing={0.25} sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                  {isComplete ? (
                    <CompleteIcon sx={{ fontSize: 18, color: accent }} />
                  ) : isBlocked ? (
                    <BlockedIcon sx={{ fontSize: 18, color: accent }} />
                  ) : isLocked ? (
                    <LockedIcon sx={{ fontSize: 18, color: accent }} />
                  ) : isCurrent ? (
                    <CurrentIcon sx={{ fontSize: 18, color: accent }} />
                  ) : (
                    <UpcomingIcon sx={{ fontSize: 18, color: alpha(accent, 0.55) }} />
                  )}
                  <Chip
                    size="small"
                    variant={isCurrent ? 'filled' : 'outlined'}
                    color={
                      isComplete
                        ? 'success'
                        : isBlocked
                          ? 'warning'
                          : isLocked
                            ? 'default'
                            : isCurrent
                              ? 'primary'
                              : 'default'
                    }
                    label={label}
                    sx={{
                      fontWeight: isCurrent ? 800 : 600,
                      maxWidth: '100%',
                    }}
                  />
                </Stack>
                {showStatusLabels ? (
                  <Box sx={{ pl: 3.25 }}>
                    <Typography
                      variant="caption"
                      data-testid={`safety-workflow-status-${step.id}`}
                      sx={{
                        display: 'block',
                        fontWeight: 800,
                        color: accent,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                      }}
                    >
                      {statusCaption
                        ? statusCaption(step.id, status)
                        : workflowStatusCaption(status)}
                    </Typography>
                    {statusHint?.(step.id, status) ? (
                      <Typography
                        variant="caption"
                        data-testid={`safety-workflow-status-hint-${step.id}`}
                        sx={{
                          display: 'block',
                          mt: 0.2,
                          lineHeight: 1.45,
                          color: 'text.secondary',
                          fontWeight: 600,
                          textTransform: 'none',
                        }}
                      >
                        {statusHint(step.id, status)}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
              </Stack>
            </React.Fragment>
          );
        })}
      </Stack>
      {showNextAction ? (
        <Typography variant="body2" sx={{ mt: 1.25, lineHeight: 1.55 }}>
          <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>
            Next step:
          </Box>{' '}
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {progress.nextActionLabel}
          </Box>
        </Typography>
      ) : null}
    </Box>
  );
};

export default SafetyWorkflowStepper;
